/**
 * Attendance roster (/events/:eventId/roster, editors): members grouped by section with RSVP and
 * check-in time; one big tap cycles present → absent → excused → (clear). Counts at the top.
 * Polls every 20 s while visible so self check-ins show up during rehearsal.
 */
import { ClipboardX, ShieldAlert, WifiOff } from 'lucide-react';
import { useMemo, useRef } from 'react';
import { useParams } from 'react-router';
import type { RosterEntryDTO } from '../../shared/dto/events';
import { isEventGone, useEvent, useRoster, useRosterOverrides } from '../api/calendar';
import { Page } from '../components/layout';
import { SECTION_COLOR } from '../components/people';
import { useToast } from '../components/toast';
import { CenteredSpinner, cx, EmptyState, ErrorBox, Spinner } from '../components/ui';
import { useI18n, useT } from '../i18n';
import { capitalize, formatDay, formatTimeRange } from '../lib/dates';
import { useQueuedRejectionToasts } from './calendar/participation';
import { nextOverride, rosterStatus, RosterRow, STATUS_KEY } from './calendar/RosterRow';
import { errorText, SECTION_ORDER, sectionGroupKey, useIsEditor } from './calendar/shared';

function Stat({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-3" style={{ borderTop: `5px solid ${tone}` }}>
      <span className="text-[30px] leading-none font-bold tabular-nums">{value}</span>
      <span className="text-[15px] font-semibold text-[var(--muted)]">{label}</span>
    </div>
  );
}

export default function EventRosterScreen() {
  const { eventId = '' } = useParams();
  const t = useT();
  const { loc, lang } = useI18n();
  const editor = useIsEditor();
  const toast = useToast();
  const evQ = useEvent(eventId);
  const ev = isEventGone(evQ.error) ? undefined : evQ.data;
  const q = useRoster(eventId, editor);
  const lastQueuedToast = useRef(0);
  useQueuedRejectionToasts();
  const { overrides, setOverride } = useRosterOverrides(eventId, (e, queued) => {
    if (queued) {
      if (Date.now() - lastQueuedToast.current > 10_000) toast.show(t('calendar.saved_offline'));
      lastQueuedToast.current = Date.now();
    } else toast.show(errorText(t, e), { tone: 'error' });
  });

  const entries = useMemo<RosterEntryDTO[]>(
    () => (q.data ?? []).map((e) => (overrides.has(e.userId) ? { ...e, override: overrides.get(e.userId) ?? null } : e)),
    [q.data, overrides],
  );

  const groups = useMemo(() => {
    const collator = new Intl.Collator('is');
    return SECTION_ORDER.map((s) => ({
      section: s,
      list: entries.filter((e) => e.section === s).sort((a, b) => collator.compare(a.fullName, b.fullName)),
    })).filter((g) => g.list.length > 0);
  }, [entries, lang]);

  const counts = useMemo(() => {
    const c = { present: 0, absent: 0, excused: 0, unmarked: 0, coming: 0, unanswered: 0 };
    for (const e of entries) {
      c[rosterStatus(e) ?? 'unmarked']++;
      if (e.effectiveRsvp === 'yes') c.coming++;
      else if (e.effectiveRsvp === null) c.unanswered++;
    }
    return c;
  }, [entries]);

  const back = `/events/${eventId}`;

  if (!editor) {
    return (
      <Page title={t('calendar.roster.title')} back={back}>
        <EmptyState icon={ShieldAlert} title={t('error.forbidden')} />
      </Page>
    );
  }

  return (
    <Page
      title={t('calendar.roster.title')}
      back={back}
      wide
      actions={q.isFetching && q.data ? <Spinner className="[&_svg]:size-5" /> : undefined}
    >
      <div className="flex flex-col gap-5">
        {ev ? (
          <div>
            <h2 className="text-[22px] leading-tight font-bold">{ev.title}</h2>
            <p className="text-[16px] text-[var(--muted)]">
              {capitalize(formatDay(ev.startsAt, loc), loc)} · <span className="tabular-nums">{formatTimeRange(ev.startsAt, ev.endsAt, loc)}</span>
            </p>
          </div>
        ) : null}

        {q.isPending && q.fetchStatus === 'paused' ? (
          <EmptyState icon={WifiOff} title={t('common.offline')}>
            {t('common.no_data_offline')}
          </EmptyState>
        ) : q.isPending ? (
          <CenteredSpinner label={t('common.loading')} />
        ) : q.isError && !q.data ? (
          <ErrorBox onRetry={() => void q.refetch()} retryLabel={t('common.retry')}>
            {errorText(t, q.error)}
          </ErrorBox>
        ) : entries.length === 0 ? (
          <EmptyState icon={ClipboardX} title={t('calendar.roster.empty')} />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              <Stat label={t(STATUS_KEY.present)} value={counts.present} tone="var(--ok)" />
              <Stat label={t(STATUS_KEY.absent)} value={counts.absent} tone="var(--danger)" />
              <Stat label={t(STATUS_KEY.excused)} value={counts.excused} tone="var(--warn)" />
              <Stat label={t(STATUS_KEY.unmarked)} value={counts.unmarked} tone="var(--line)" />
              <Stat label={t('calendar.roster.coming')} value={counts.coming} tone="var(--accent)" />
              <Stat label={t('calendar.rsvp.none')} value={counts.unanswered} tone="var(--line)" />
            </div>
            <p className="text-[15px] text-[var(--muted)]">{t('calendar.roster.tap_hint')}</p>

            {groups.map((g) => {
              const present = g.list.filter((e) => rosterStatus(e) === 'present').length;
              return (
                <section key={g.section ?? 'none'} aria-label={t(sectionGroupKey(g.section))}>
                  <h3 className="mb-2 flex items-center gap-2 text-[18px] font-bold">
                    <span
                      className={cx('inline-block h-6 w-1.5 rounded-full', !g.section && 'bg-[var(--line)]')}
                      style={g.section ? { background: SECTION_COLOR[g.section] } : undefined}
                      aria-hidden
                    />
                    {t(sectionGroupKey(g.section))}
                    <span className="text-[15px] font-semibold text-[var(--muted)]">
                      · {t('calendar.roster.section_count', { present, total: g.list.length })}
                    </span>
                  </h3>
                  <ul className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
                    {g.list.map((e) => (
                      <li key={e.userId}>
                        <RosterRow e={e} onTap={() => setOverride(e.userId, nextOverride(e))} />
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </>
        )}
      </div>
    </Page>
  );
}
